/**
 * Operator job controls. No in-force row means the compiled default: callers
 * that see `untouched` must keep the pre-change arguments and SQL.
 *
 * Shed never pauses P0 or P1. A P0 pause is refused unless `force` is set.
 * Every override expires; `until - set_at` is at most 48 hours.
 * A null `until` is refused. There is no open-ended row.
 */
import {
  LIFECYCLE_EVENTS_CLEANUP_BATCH_ROWS,
  LIFECYCLE_EVENTS_CLEANUP_MAX_BATCHES,
  LIFECYCLE_EVENTS_CLEANUP_TIME_BUDGET_MS,
} from './internal-lifecycle-events.ts';
import {
  LIFECYCLE_METRICS_CLEANUP_BATCH_ROWS,
  LIFECYCLE_METRICS_CLEANUP_MAX_BATCHES,
  LIFECYCLE_METRICS_CLEANUP_TIME_BUDGET_MS,
} from './internal-lifecycle-metrics.ts';
import {
  FINANCE_PNL_MATERIALIZE_WALL_MAX_MS,
  FINANCE_PNL_MATERIALIZE_WALL_MIN_MS,
  FINANCE_PNL_MATERIALIZE_WALL_MS,
} from './internal-cron-finance-pnl.ts';
import { RATING_EVENTS_CLEANUP_MAX_ROWS } from './internal-rating.ts';
import {
  RATED_ROLLUP_FOLD_AUDIT_HOURS_CEILING,
  RATED_ROLLUP_FOLD_AUDIT_HOURS_PER_CALL,
  RATED_ROLLUP_FOLD_MAX_HOURS_CEILING,
  RATED_ROLLUP_FOLD_MAX_HOURS_PER_CALL,
  RATED_ROLLUP_FOLD_STATEMENT_TIMEOUT_MAX_MS,
  RATED_ROLLUP_FOLD_STATEMENT_TIMEOUT_MIN_MS,
  RATED_ROLLUP_FOLD_STATEMENT_TIMEOUT_MS,
  RATED_ROLLUP_FOLD_WALL_MAX_MS,
  RATED_ROLLUP_FOLD_WALL_MIN_MS,
  RATED_ROLLUP_FOLD_WALL_MS,
} from './internal-cron-rated-rollup.ts';
import {
  REVENUE_HOURLY_FOLD_AUDIT_HOURS_CEILING,
  REVENUE_HOURLY_FOLD_AUDIT_HOURS_PER_CALL,
  REVENUE_HOURLY_FOLD_MAX_HOURS_CEILING,
  REVENUE_HOURLY_FOLD_MAX_HOURS_PER_CALL,
  REVENUE_HOURLY_FOLD_STATEMENT_TIMEOUT_MAX_MS,
  REVENUE_HOURLY_FOLD_STATEMENT_TIMEOUT_MIN_MS,
  REVENUE_HOURLY_FOLD_STATEMENT_TIMEOUT_MS,
  REVENUE_HOURLY_FOLD_WALL_MAX_MS,
  REVENUE_HOURLY_FOLD_WALL_MIN_MS,
  REVENUE_HOURLY_FOLD_WALL_MS,
} from './internal-cron-revenue-hourly.ts';
import {
  REVENUE_FUNDING_MATERIALIZE_MAX_DAYS_CEILING,
  REVENUE_FUNDING_MATERIALIZE_MAX_DAYS_PER_CALL,
  REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MAX_MS,
  REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MIN_MS,
  REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MS,
  REVENUE_FUNDING_MATERIALIZE_WALL_MAX_MS,
  REVENUE_FUNDING_MATERIALIZE_WALL_MIN_MS,
  REVENUE_FUNDING_MATERIALIZE_WALL_MS,
} from './internal-cron-revenue-funding.ts';
import {
  LEDGER_ARCHIVE_BUDGET_MS,
  LEDGER_ARCHIVE_MIN_BUDGET_MS,
  LEDGER_ARCHIVE_WINDOW_END_MINUTE,
  LEDGER_ARCHIVE_WINDOW_START_MINUTE,
} from './ledger-archive.ts';
import { BATCH_ALLOCATOR_CONSUMERS_CEILING } from './internal-batch-allocate.ts';
import { BALANCE_SOURCE_HEALTH_REFRESH_INTERVAL_MINUTES } from './internal-ops-risks.ts';
import { RATING_DRAIN_CONSUMERS_CEILING } from './internal-rating.ts';
import { REAPER_LIMIT_CEILINGS } from './internal-reaper-limits.ts';
import {
  USAGE_ROLLUP_COLD_BACKFILL_DAYS,
  USAGE_ROLLUP_BACKFILL_DAYS_PER_FOLD,
  USAGE_ROLLUP_FOLD_BUDGET_MS,
  USAGE_ROLLUP_FOLD_MAX_DAYS_CAP,
  USAGE_ROLLUP_LOOKBACK_DAYS_MAX,
  USAGE_ROLLUP_MAX_DAYS_PER_FOLD,
} from './internal-usage-rollups.ts';
import {
  USAGE_ORG_HOURLY_FOLD_BUDGET_MAX_MS,
  USAGE_ORG_HOURLY_FOLD_BUDGET_MS,
  USAGE_ORG_HOURLY_FOLD_MAX_HOURS_CAP,
  USAGE_ORG_HOURLY_FOLD_MIN_BUDGET_MS,
  USAGE_ORG_HOURLY_MAX_HOURS_PER_FOLD,
  USAGE_ORG_HOURLY_MAX_VERIFY_HOURS,
  USAGE_ORG_HOURLY_MAX_DIM_BACKFILL_HOURS,
  USAGE_ORG_HOURLY_RECHECK_HOURS,
} from './internal-usage-org-hourly.ts';

/** ledger_compare's compiled route budget (platform `LEDGER_COMPARE_ROUTE_BUDGET_MS`). */
export const LEDGER_COMPARE_CONTROL_BUDGET_DEFAULT_MS = 100_000;
/** Below this the compare throws before a window (`LEDGER_COMPARE_MIN_WINDOW_MS`). */
export const LEDGER_COMPARE_CONTROL_BUDGET_MIN_MS = 5_000;
/**
 * Strictly under the control-loops fetch timeout (120s) so a maxed knob
 * still returns before the caller gives up.
 */
export const LEDGER_COMPARE_CONTROL_BUDGET_MAX_MS = 110_000;

/** `rating_drain` rounds: default 40, ceiling floor(45000/700) = 64. */
export const RATING_DRAIN_CONTROL_MAX_ROUNDS_DEFAULT = 40;
export const RATING_DRAIN_CONTROL_MAX_ROUNDS_MAX = 64;
/** `batch_allocator` rounds: default 200, ceiling floor(45000/150) = 300. `0` stays an env pause, not this knob. */
export const BATCH_ALLOCATOR_CONTROL_MAX_ROUNDS_DEFAULT = 200;
export const BATCH_ALLOCATOR_CONTROL_MAX_ROUNDS_MAX = 300;
/** `credit_draw_catchup` share. Unset env is 20000; 0 disables the catch-up while it stays armed. */
export const CREDIT_DRAW_CATCHUP_CONTROL_BUDGET_DEFAULT_MS = 20_000;
export const CREDIT_DRAW_CATCHUP_CONTROL_BUDGET_MAX_MS = 25_000;
/** `credit_draw_sweep_lane` share. Unset / 0 is off. 1–4999 is off at the existing parser. */
export const CREDIT_DRAW_SWEEP_LANE_CONTROL_BUDGET_MAX_MS = 45_000;
/** `balance_source_health_refresh` skip stride. The schedule stays at 360; a larger knob skips cycles. */
export const BALANCE_SOURCE_HEALTH_CONTROL_INTERVAL_MAX_MINUTES = 1_440;

/** `usage_rollup_fold` compiled cadence (minutes). The interval knob can only hold or speed it up. */
export const USAGE_ROLLUP_FOLD_INTERVAL_MINUTES = 15;
/** The shortest fold call budget the knob accepts. The compiled budget is also its ceiling: a bigger budget is not a way to go faster. */
export const USAGE_ROLLUP_FOLD_CONTROL_BUDGET_MIN_MS = 20_000;

/** `ledger_bca_key_backfill`: the plan's hourly WAL ceiling (2 GB). The knob only lowers it. */
export const BCA_KEY_BACKFILL_CONTROL_WAL_MB_MAX = 2_048;

export const JOB_CONTROL_MAX_MS = 48 * 60 * 60 * 1000;
export const JOB_CONTROL_CACHE_MS = 30_000;
export const JOB_BOOST_HARD_MAX = 8;
/** Preset multipliers. Anything else is refused at write time. */
export const JOB_BOOST_PRESETS = Object.freeze({ '2': 2, '4': 4 });

export const PLATFORM_JOB_CONTROLS_TABLE = 'platform_job_controls';
export const GATEWAY_JOB_CONTROLS_TABLE = 'gateway_job_controls';
export const PLATFORM_JOB_CONTROL_EVENTS_TABLE = 'platform_job_control_events';
export const GATEWAY_JOB_CONTROL_EVENTS_TABLE = 'gateway_job_control_events';

export function jobControlsInForceSql(table: string): string {
  return `SELECT key, value, until FROM ${table} WHERE cleared_at IS NULL AND until > now()`;
}

export const JOB_CONTROLS_IN_FORCE_SQL = jobControlsInForceSql(PLATFORM_JOB_CONTROLS_TABLE);

export type JobPriority = 'P0' | 'P1' | 'P2' | 'P3';
export type JobOwner = 'gateway' | 'platform';
export type JobBoostPreset = keyof typeof JOB_BOOST_PRESETS;

export interface JobKnobSpec {
  readonly name: string;
  readonly min: number;
  readonly max: number;
  readonly defaultValue: number;
  /** Boost multiplies this knob, then the hard max clamps it. */
  readonly boost: boolean;
  /**
   * Absolute value for boost preset `'2'` or `'4'`, used instead of
   * `floor(default * preset)` when that preset is in force, then clamped to
   * min..max. A missing preset keeps the multiply. A result equal to the
   * default stays source `default` and does not mark the job touched.
   */
  readonly boostTargets?: Partial<Record<'2' | '4', number>>;
  /**
   * Only the K=1 drain reads this knob. Ops refuses the write. A row that
   * is already stored applies only when consumers resolve to 1.
   */
  readonly k1Only?: boolean;
  /**
   * Values must fall in one of these inclusive ranges. Anything else inside
   * min..max is refused, not clamped into a hole the parser treats as off.
   */
  readonly accept?: readonly { readonly min: number; readonly max: number }[];
  /** Worker binding whose integer is the live base for this knob. */
  readonly envKey?: string;
  /** Process that actually has `envKey`. Another process must not invent the number. */
  readonly envHome?: 'platform' | 'gateway' | 'control-loops';
}

export interface JobWindow {
  readonly fromMinute: number;
  readonly toMinute: number;
}

export interface JobSpec {
  readonly name: string;
  readonly owner: JobOwner;
  readonly priority: JobPriority;
  readonly summary: string;
  /**
   * What an operator stops by pausing this job. Printed by `ops jobs pause`
   * for P0. Absent means the CLI prints no extra line.
   */
  readonly pauseEffect?: string;
  readonly knobs: readonly JobKnobSpec[];
  /** Shanghai minutes [from, to) the job does not run. Absent = no such window. */
  readonly quietWindow?: JobWindow;
  /** Shanghai minutes [from, to) the job is allowed to run. Absent = no such window. */
  readonly runWindow?: JobWindow;
}

const QUIET_0207: JobWindow = { fromMinute: 120, toMinute: 420 };

function knob(
  name: string,
  min: number,
  max: number,
  defaultValue: number,
  boost = true,
  extra?: Pick<JobKnobSpec, 'k1Only' | 'accept' | 'envKey' | 'envHome' | 'boostTargets'>,
): JobKnobSpec {
  return {
    name,
    min,
    max,
    defaultValue,
    boost,
    ...(extra?.k1Only === true ? { k1Only: true } : {}),
    ...(extra?.accept === undefined ? {} : { accept: extra.accept }),
    ...(extra?.envKey === undefined ? {} : { envKey: extra.envKey }),
    ...(extra?.envHome === undefined ? {} : { envHome: extra.envHome }),
    ...(extra?.boostTargets === undefined ? {} : { boostTargets: extra.boostTargets }),
  };
}

/**
 * Registered jobs. The three usage rollup folds are P3: shed 1 and shed 2
 * both stop them. Their catch-up loops are not in this branch.
 */
export const JOB_SPECS: readonly JobSpec[] = Object.freeze([
  {
    name: 'ledger_archive',
    owner: 'platform',
    priority: 'P1',
    summary: 'Money-table archive export and verify. Pause stays on the existing ledger-archive sentinel, not this table.',
    knobs: [knob('time_budget_ms', LEDGER_ARCHIVE_MIN_BUDGET_MS, LEDGER_ARCHIVE_BUDGET_MS, LEDGER_ARCHIVE_BUDGET_MS, false, { envKey: 'LEDGER_ARCHIVE_BUDGET_MS', envHome: 'control-loops' })],
    runWindow: { fromMinute: LEDGER_ARCHIVE_WINDOW_START_MINUTE, toMinute: LEDGER_ARCHIVE_WINDOW_END_MINUTE },
  },
  {
    name: 'ledger_compare',
    owner: 'platform',
    priority: 'P2',
    summary: 'Read-only ledger compare. Shed 2 skips it. time_budget_ms shortens the route deadline; it is not boosted.',
    knobs: [knob(
      'time_budget_ms',
      LEDGER_COMPARE_CONTROL_BUDGET_MIN_MS,
      LEDGER_COMPARE_CONTROL_BUDGET_MAX_MS,
      LEDGER_COMPARE_CONTROL_BUDGET_DEFAULT_MS,
      false,
    )],
  },
  {
    name: 'overflow_catchup',
    owner: 'platform',
    priority: 'P0',
    summary: 'Re-attribute upstream spend that landed before its funding batch. Shed never pauses it.',
    pauseEffect: 'Pausing this stops re-attributing upstream spend that landed before its funding batch.',
    knobs: [],
  },
  {
    name: 'credit_draw_catchup',
    owner: 'platform',
    priority: 'P0',
    summary: 'Catch up lot attribution for charges. Shed never pauses it. budget_ms is CREDIT_DRAW_CATCHUP_BUDGET_MS.',
    pauseEffect: 'Pausing this stops catching up lot attribution for charges.',
    knobs: [knob('budget_ms', 0, CREDIT_DRAW_CATCHUP_CONTROL_BUDGET_MAX_MS, CREDIT_DRAW_CATCHUP_CONTROL_BUDGET_DEFAULT_MS, true, { envKey: 'CREDIT_DRAW_CATCHUP_BUDGET_MS', envHome: 'control-loops' })],
  },
  {
    name: 'credit_draw_sweep',
    owner: 'platform',
    priority: 'P0',
    summary: 'Sweep credit-draw attribution. Rounds (12) and the 90s budget are compiled constants, not env knobs. Shed never pauses it.',
    pauseEffect: 'Pausing this stops the credit-draw attribution sweep.',
    knobs: [],
  },
  {
    name: 'credit_draw_sweep_lane',
    owner: 'platform',
    priority: 'P0',
    summary: 'Per-minute credit-draw lane. budget_ms is 0 (off) or 5000–45000. 1–4999 is refused. Boost does not arm it. Shed never pauses it.',
    pauseEffect: 'Pausing this stops the per-minute credit-draw lane.',
    knobs: [knob('budget_ms', 0, CREDIT_DRAW_SWEEP_LANE_CONTROL_BUDGET_MAX_MS, 0, false, {
      accept: [
        { min: 0, max: 0 },
        { min: 5_000, max: CREDIT_DRAW_SWEEP_LANE_CONTROL_BUDGET_MAX_MS },
      ],
      envKey: 'CREDIT_DRAW_SWEEP_LANE_MS',
      envHome: 'control-loops',
    })],
  },
  {
    name: 'rating_drain',
    owner: 'platform',
    priority: 'P0',
    summary: 'Rate usage into the ledger. consumers is the env knob. max_rounds is K=1 only and ops refuses it. Shed never pauses it.',
    pauseEffect: 'Pausing this stops rating usage into the ledger, including the K>=2 lanes route.',
    knobs: [
      knob('max_rounds', 1, RATING_DRAIN_CONTROL_MAX_ROUNDS_MAX, RATING_DRAIN_CONTROL_MAX_ROUNDS_DEFAULT, true, { k1Only: true, envKey: 'RATING_DRAIN_MAX_ROUNDS', envHome: 'control-loops' }),
      knob('consumers', 1, RATING_DRAIN_CONSUMERS_CEILING, 1, true, { envKey: 'RATING_DRAIN_CONSUMERS', envHome: 'control-loops' }),
    ],
  },
  {
    name: 'batch_allocator',
    owner: 'platform',
    priority: 'P0',
    summary: 'Attribute procurement cost. max_rounds and consumers (shard count) match the env knobs. Shed never pauses it.',
    pauseEffect: 'Pausing this stops attributing procurement cost.',
    knobs: [
      knob('max_rounds', 1, BATCH_ALLOCATOR_CONTROL_MAX_ROUNDS_MAX, BATCH_ALLOCATOR_CONTROL_MAX_ROUNDS_DEFAULT, true, { envKey: 'BATCH_ALLOCATOR_MAX_ROUNDS', envHome: 'control-loops' }),
      knob('consumers', 1, BATCH_ALLOCATOR_CONSUMERS_CEILING, 1, true, { envKey: 'BATCH_ALLOCATOR_CONSUMERS', envHome: 'control-loops' }),
    ],
  },
  {
    name: 'pub_auth_reconcile',
    owner: 'platform',
    priority: 'P0',
    summary: 'Republish tenant auth. Shed never pauses it (60 minutes dark and tenants 503).',
    pauseEffect: 'Pausing this stops republishing tenant auth. After about 60 minutes, tenants get 503.',
    knobs: [],
  },
  {
    name: 'usage_logs_partitions',
    owner: 'gateway',
    priority: 'P0',
    summary: 'Ensure usage_logs partitions. Shed never pauses it.',
    pauseEffect: 'Pausing this stops ensuring usage_logs partitions. After about 14 days, writes into the next day fail.',
    knobs: [],
  },
  {
    name: 'usage_events_partitions',
    owner: 'gateway',
    priority: 'P0',
    summary: 'Ensure usage_events partitions. Shed never pauses it.',
    pauseEffect: 'Pausing this stops ensuring usage_events partitions. After about 14 days, writes into the next day fail.',
    knobs: [],
  },
  {
    name: 'message_traces_partitions',
    owner: 'gateway',
    priority: 'P0',
    summary: 'Ensure message_traces partitions. Shed never pauses it.',
    pauseEffect: 'Pausing this stops ensuring message_traces partitions. After about 14 days, writes into the next day fail.',
    knobs: [],
  },
  {
    name: 'usage_rated_events_partitions',
    owner: 'platform',
    priority: 'P0',
    summary: 'Ensure usage_rated_events partitions. Shed never pauses it.',
    pauseEffect: 'Pausing this stops ensuring usage_rated_events partitions. After about 14 days, writes into the next day fail.',
    knobs: [],
  },
  {
    name: 'lifecycle_events_cleanup',
    owner: 'gateway',
    priority: 'P3',
    summary: 'Delete aged request_lifecycle_event_batches.',
    knobs: [
      knob('batch_rows', 1, REAPER_LIMIT_CEILINGS.LIFECYCLE_EVENTS_CLEANUP_BATCH_ROWS, LIFECYCLE_EVENTS_CLEANUP_BATCH_ROWS, true, { envKey: 'LIFECYCLE_EVENTS_CLEANUP_BATCH_ROWS', envHome: 'gateway' }),
      knob('max_batches', 1, REAPER_LIMIT_CEILINGS.LIFECYCLE_EVENTS_CLEANUP_MAX_BATCHES, LIFECYCLE_EVENTS_CLEANUP_MAX_BATCHES, true, { envKey: 'LIFECYCLE_EVENTS_CLEANUP_MAX_BATCHES', envHome: 'gateway' }),
      knob('time_budget_ms', 1, REAPER_LIMIT_CEILINGS.LIFECYCLE_EVENTS_CLEANUP_TIME_BUDGET_MS, LIFECYCLE_EVENTS_CLEANUP_TIME_BUDGET_MS, true, { envKey: 'LIFECYCLE_EVENTS_CLEANUP_TIME_BUDGET_MS', envHome: 'gateway' }),
    ],
  },
  {
    name: 'lifecycle_metrics_cleanup',
    owner: 'gateway',
    priority: 'P3',
    summary: 'Delete aged request_lifecycle_metrics.',
    knobs: [
      knob('batch_rows', 1, REAPER_LIMIT_CEILINGS.LIFECYCLE_METRICS_CLEANUP_BATCH_ROWS, LIFECYCLE_METRICS_CLEANUP_BATCH_ROWS, true, { envKey: 'LIFECYCLE_METRICS_CLEANUP_BATCH_ROWS', envHome: 'gateway' }),
      knob('max_batches', 1, REAPER_LIMIT_CEILINGS.LIFECYCLE_METRICS_CLEANUP_MAX_BATCHES, LIFECYCLE_METRICS_CLEANUP_MAX_BATCHES, true, { envKey: 'LIFECYCLE_METRICS_CLEANUP_MAX_BATCHES', envHome: 'gateway' }),
      knob('time_budget_ms', 1, REAPER_LIMIT_CEILINGS.LIFECYCLE_METRICS_CLEANUP_TIME_BUDGET_MS, LIFECYCLE_METRICS_CLEANUP_TIME_BUDGET_MS, true, { envKey: 'LIFECYCLE_METRICS_CLEANUP_TIME_BUDGET_MS', envHome: 'gateway' }),
    ],
  },
  {
    name: 'message_trace_cleanup',
    owner: 'gateway',
    priority: 'P3',
    summary: 'Delete aged message traces.',
    knobs: [
      knob('batch_rows', 1, REAPER_LIMIT_CEILINGS.TRACE_CLEANUP_BATCH_ROWS, 2_000, true, { envKey: 'TRACE_CLEANUP_BATCH_ROWS', envHome: 'gateway' }),
      knob('max_batches', 1, REAPER_LIMIT_CEILINGS.TRACE_CLEANUP_MAX_BATCHES, 8, true, { envKey: 'TRACE_CLEANUP_MAX_BATCHES', envHome: 'gateway' }),
      knob('time_budget_ms', 1, REAPER_LIMIT_CEILINGS.TRACE_CLEANUP_TIME_BUDGET_MS, 1_500, true, { envKey: 'TRACE_CLEANUP_TIME_BUDGET_MS', envHome: 'gateway' }),
    ],
  },
  {
    name: 'rating_events_cleanup',
    owner: 'gateway',
    priority: 'P3',
    summary: 'Delete aged usage_events rows that already have a rated sibling.',
    knobs: [knob('max_rows', 1, REAPER_LIMIT_CEILINGS.RATING_EVENTS_CLEANUP_MAX_ROWS, RATING_EVENTS_CLEANUP_MAX_ROWS, true, { envKey: 'RATING_EVENTS_CLEANUP_MAX_ROWS', envHome: 'gateway' })],
  },
  {
    name: 'usage_logs_cn_cleanup',
    owner: 'gateway',
    priority: 'P3',
    summary: 'CN usage_logs retention after Tokyo confirms the row.',
    knobs: [],
  },
  {
    name: 'usage_events_day_verify',
    owner: 'gateway',
    priority: 'P3',
    summary: 'Continue the usage_events whole-day truncate verify walk.',
    knobs: [],
    quietWindow: QUIET_0207,
  },
  {
    name: 'rc_identity_daily_fold',
    owner: 'platform',
    priority: 'P3',
    summary: 'Seal one Shanghai day of router-client identity counts.',
    knobs: [],
    quietWindow: QUIET_0207,
  },
  {
    name: 'balance_source_health_refresh',
    owner: 'platform',
    priority: 'P3',
    summary: 'Recompute balance-source health (four full balance_transactions scans). Pausable. interval_minutes skips scheduled cycles; the scheduler interval stays 360.',
    knobs: [knob(
      'interval_minutes',
      BALANCE_SOURCE_HEALTH_REFRESH_INTERVAL_MINUTES,
      BALANCE_SOURCE_HEALTH_CONTROL_INTERVAL_MAX_MINUTES,
      BALANCE_SOURCE_HEALTH_REFRESH_INTERVAL_MINUTES,
      false,
    )],
  },
  {
    name: 'usage_org_hourly_fold',
    owner: 'platform',
    priority: 'P3',
    summary: 'Seal org-hour usage rollups (platform, Tokyo database). Shed 1 stops it. Knobs: max_hours (default 4, preset 4 seals 25 new hours and backfills the same number of unit hours), budget_ms (default 20000, preset 4 is 45000), interval_minutes (default 5, preset 4 is 1), recheck_hours (default 24, preset 4 is 4), verify_hours (default 4, set directly, not boosted; hours verified per call), dim_hours (default 4, set directly, not boosted; the dimension layer backfill). Preset 4 is 1 minute, 25 hours, a 45 s budget, recheck 4. It does not move verify_hours.',
    pauseEffect: 'Pausing this stops sealing org-hour usage rollups.',
    knobs: [
      knob('max_hours', 1, USAGE_ORG_HOURLY_FOLD_MAX_HOURS_CAP, USAGE_ORG_HOURLY_MAX_HOURS_PER_FOLD, true, { boostTargets: { '4': 25 } }),
      knob('budget_ms', USAGE_ORG_HOURLY_FOLD_MIN_BUDGET_MS, USAGE_ORG_HOURLY_FOLD_BUDGET_MAX_MS, USAGE_ORG_HOURLY_FOLD_BUDGET_MS, true),
      // Max is the compiled 5-minute cadence: the knob can only hold or speed up.
      knob('interval_minutes', 1, 5, 5, true, { boostTargets: { '4': 1 } }),
      knob('recheck_hours', 0, USAGE_ORG_HOURLY_RECHECK_HOURS, USAGE_ORG_HOURLY_RECHECK_HOURS, true, { boostTargets: { '4': 4 } }),
      // Hour x day-dimension layer backfill (gateway 0140): its own cap, so it never takes the hot seal's slots.
      // Not boosted: the preset-4 request body is a documented operator check and stays as it is; set this knob directly.
      knob('dim_hours', 1, USAGE_ORG_HOURLY_FOLD_MAX_HOURS_CAP, USAGE_ORG_HOURLY_MAX_DIM_BACKFILL_HOURS, false),
      // Hours verified per call. Not boosted: unset stays the compiled 4 and is omitted from the request.
      knob('verify_hours', 1, USAGE_ORG_HOURLY_FOLD_MAX_HOURS_CAP, USAGE_ORG_HOURLY_MAX_VERIFY_HOURS, false),
    ],
  },
  {
    name: 'finance_pnl_materialize',
    owner: 'platform',
    priority: 'P3',
    summary: 'Compute the 5-minute PnL usage pieces and seal Shanghai days past the 48 h grace (the /pnl usage numbers). Every 2 minutes, concurrent, sized from what the tick has left. Shed 1 stops it. Knob: wall_ms (default 20000, can only be lowered) shortens the route wall; a shorter tick shortens it too, without the table. The page falls back to live statements for whatever is not stored, so a pause is slower reads, not wrong numbers.',
    pauseEffect: 'Pausing this stops the PnL piece store and day seals; /pnl answers unsealed days from live statements and stays slow until it resumes.',
    knobs: [
      knob('wall_ms', FINANCE_PNL_MATERIALIZE_WALL_MIN_MS, FINANCE_PNL_MATERIALIZE_WALL_MAX_MS, FINANCE_PNL_MATERIALIZE_WALL_MS, false),
    ],
  },
  {
    name: 'revenue_funding_materialize',
    owner: 'platform',
    priority: 'P3',
    summary: 'Seal Shanghai-day revenue funding rollups (the funding split of /v1/ops/revenue/summary). Shed 1 stops it. Knobs: max_days (default 4, preset 4 is 16), statement_timeout_ms (default 8000, can only be lowered), wall_ms (default 25000, can only be lowered). A summary window reads each unsealed day live, so a backlog of unsealed days is what makes it slow.',
    pauseEffect: 'Pausing this stops sealing revenue funding rollup days; /v1/ops/revenue/summary reads the unsealed days live and stays slow.',
    knobs: [
      knob('max_days', 1, REVENUE_FUNDING_MATERIALIZE_MAX_DAYS_CEILING, REVENUE_FUNDING_MATERIALIZE_MAX_DAYS_PER_CALL, true, { boostTargets: { '2': 8, '4': REVENUE_FUNDING_MATERIALIZE_MAX_DAYS_CEILING } }),
      knob('statement_timeout_ms', REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MIN_MS, REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MAX_MS, REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MS, false),
      knob('wall_ms', REVENUE_FUNDING_MATERIALIZE_WALL_MIN_MS, REVENUE_FUNDING_MATERIALIZE_WALL_MAX_MS, REVENUE_FUNDING_MATERIALIZE_WALL_MS, false),
    ],
  },
  {
    name: 'rated_hour_fold',
    owner: 'platform',
    priority: 'P3',
    summary: 'Fold sealed Shanghai hours of usage_rated_events into usage_rated_hour. Dispatched only while RATED_HOUR_FOLD_ARMED is 1. Shed 1 stops it. One call folds one hot Shanghai day in one statement, or one cold day. A day that times out falls back to single hours; a failed hour is parked. Readers stay on their old statements until RATED_HOUR_READS is pair or on.',
    pauseEffect: 'Pausing this stops folding hours. Readers on RATED_HOUR_READS=on keep serving folded hours only while usage_rated_hour_state.updated_at is inside 3 hours, then fall back to the live statement.',
    knobs: [],
  },
  {
    name: 'rated_rollup_fold',
    owner: 'platform',
    priority: 'P3',
    summary: 'Fold one-hour slices of usage_rated_events into the day rollups the ops throughput series and the email notice audience read (and the pricing anchor uses with a hot-table re-check). Dispatched only while RATED_ROLLUP_FOLD_ARMED is 1. Shed 1 stops it. Knobs: max_hours (slices per call, default 6, preset 2 is 12, preset 4 is 24; late-marked slices first, then missing slices newest first), audit_hours (folded slices re-folded per call, default 1, preset 4 is 4). statement_timeout_ms and wall_ms can only be lowered: a slice runs under the same 8 s statement budget as the funding rollup and no preset raises it. A slice that times out is parked with a backoff.',
    pauseEffect: 'Pausing this stops folding rated slices; the readers keep serving the days already folded while the fold state is fresh (3 h), then read the hot table and the cold dataset as before.',
    knobs: [
      knob('max_hours', 1, RATED_ROLLUP_FOLD_MAX_HOURS_CEILING, RATED_ROLLUP_FOLD_MAX_HOURS_PER_CALL, true, { boostTargets: { '2': 12, '4': RATED_ROLLUP_FOLD_MAX_HOURS_CEILING } }),
      knob('audit_hours', 0, RATED_ROLLUP_FOLD_AUDIT_HOURS_CEILING, RATED_ROLLUP_FOLD_AUDIT_HOURS_PER_CALL, true, { boostTargets: { '4': RATED_ROLLUP_FOLD_AUDIT_HOURS_CEILING } }),
      knob('statement_timeout_ms', RATED_ROLLUP_FOLD_STATEMENT_TIMEOUT_MIN_MS, RATED_ROLLUP_FOLD_STATEMENT_TIMEOUT_MAX_MS, RATED_ROLLUP_FOLD_STATEMENT_TIMEOUT_MS, false),
      knob('wall_ms', RATED_ROLLUP_FOLD_WALL_MIN_MS, RATED_ROLLUP_FOLD_WALL_MAX_MS, RATED_ROLLUP_FOLD_WALL_MS, false),
    ],
  },
  {
    name: 'revenue_hourly_fold',
    owner: 'platform',
    priority: 'P3',
    summary: 'Fold UTC hours of the revenue live arc (funding attribution per brand, by-model/daily measures) into the hour rollups of migration 0121 that /summary, /summary/by-brand and /by-model/daily read under REVENUE_HOURLY_READ. Dispatched every 10 minutes only while REVENUE_HOURLY_FOLD_ARMED is 1. Shed 1 stops it. Knobs: max_hours (hours per call, default 6, preset 2 is 12, preset 4 is 24; late-changed hours first, then missing hours newest first), audit_hours (sealed hours recomputed and compared per call, default 3, preset 4 is 8; never-audited hours first). statement_timeout_ms and wall_ms can only be lowered: an hour runs under the same 8 s statement budget as the funding rollup and no preset raises it. An hour that times out is parked with a backoff. The backfill covers the shadow line minus 2 days up to now (about 16 days).',
    pauseEffect: 'Pausing this stops folding hours; the readers keep serving the hours already sealed while the fold state is fresh (1 h), then read the live statements as before.',
    knobs: [
      knob('max_hours', 1, REVENUE_HOURLY_FOLD_MAX_HOURS_CEILING, REVENUE_HOURLY_FOLD_MAX_HOURS_PER_CALL, true, { boostTargets: { '2': 12, '4': REVENUE_HOURLY_FOLD_MAX_HOURS_CEILING } }),
      knob('audit_hours', 0, REVENUE_HOURLY_FOLD_AUDIT_HOURS_CEILING, REVENUE_HOURLY_FOLD_AUDIT_HOURS_PER_CALL, true, { boostTargets: { '4': REVENUE_HOURLY_FOLD_AUDIT_HOURS_CEILING } }),
      knob('statement_timeout_ms', REVENUE_HOURLY_FOLD_STATEMENT_TIMEOUT_MIN_MS, REVENUE_HOURLY_FOLD_STATEMENT_TIMEOUT_MAX_MS, REVENUE_HOURLY_FOLD_STATEMENT_TIMEOUT_MS, false),
      knob('wall_ms', REVENUE_HOURLY_FOLD_WALL_MIN_MS, REVENUE_HOURLY_FOLD_WALL_MAX_MS, REVENUE_HOURLY_FOLD_WALL_MS, false),
    ],
  },
  {
    name: 'usage_rollup_fold',
    owner: 'platform',
    priority: 'P3',
    summary: 'Fold usage_logs (hot) into the day rollups; a day whose hot rows are gone is left to the cold path. Shed 1 stops it. The handler still skips Shanghai 02:00–07:00 on its own. Knobs: max_days (default 2, preset 2/4 doubles), backfill_days (default 1, preset 2/4 doubles), interval_minutes (default 15, preset 2 is 5, preset 4 is 3), budget_ms (default 180000 is also the ceiling), lookback_days (default 91), narrow_days (default 0 = off: derive the narrow stamps of folded days from their summaries). A pause stops the next call, not the one in flight. Speed comes from more days per slot and a denser slot, never a bigger call budget.',
    pauseEffect: 'Pausing this stops folding usage_logs into the day rollups.',
    knobs: [
      knob('max_days', 1, USAGE_ROLLUP_FOLD_MAX_DAYS_CAP, USAGE_ROLLUP_MAX_DAYS_PER_FOLD, true),
      knob('backfill_days', 0, USAGE_ROLLUP_FOLD_MAX_DAYS_CAP, USAGE_ROLLUP_BACKFILL_DAYS_PER_FOLD, true),
      // Max is the compiled 15-minute cadence: the knob can only hold or speed up. A call is bounded by its budget
      // (180 s) and the fold lock, so the densest preset (3) never stacks calls.
      knob('interval_minutes', 1, USAGE_ROLLUP_FOLD_INTERVAL_MINUTES, USAGE_ROLLUP_FOLD_INTERVAL_MINUTES, true, { boostTargets: { '2': 5, '4': 3 } }),
      knob('budget_ms', USAGE_ROLLUP_FOLD_CONTROL_BUDGET_MIN_MS, USAGE_ROLLUP_FOLD_BUDGET_MS, USAGE_ROLLUP_FOLD_BUDGET_MS, false),
      knob('lookback_days', 1, USAGE_ROLLUP_LOOKBACK_DAYS_MAX, USAGE_ROLLUP_COLD_BACKFILL_DAYS, false),
      knob('narrow_days', 0, USAGE_ROLLUP_FOLD_MAX_DAYS_CAP, 0, false),
    ],
  },
  {
    name: 'usage_rollup_cold_refold',
    owner: 'platform',
    priority: 'P3',
    summary: 'Rebuild day rollups from the cold layer. Shed 1 stops it.',
    pauseEffect: 'Pausing this stops rebuilding day rollups from the cold layer.',
    knobs: [],
  },
  {
    // Ledger plan car 2 (R-2 P0-1): runs only while platform LEDGER_BCA_KEY_BACKFILL=on.
    name: 'ledger_bca_key_backfill',
    owner: 'platform',
    priority: 'P3',
    summary: 'Backfill balance_charge_allocations.charge_created_at / family_kind from the parent charge, one heap block range per batch. Runs only while LEDGER_BCA_KEY_BACKFILL=on. wal_mb_per_hour lowers the hourly WAL ceiling (2048 is the code maximum).',
    pauseEffect: 'Pausing this stops the bca charge-key backfill; it resumes from its saved block cursor.',
    // 02:00–07:00 Shanghai is a code constant of the job, not a window row: no quiet_window knob.
    knobs: [knob('wal_mb_per_hour', 64, BCA_KEY_BACKFILL_CONTROL_WAL_MB_MAX, BCA_KEY_BACKFILL_CONTROL_WAL_MB_MAX, false)],
  },
]);

const SPECS_BY_NAME = new Map(JOB_SPECS.map((spec) => [spec.name, spec]));

export function jobSpec(name: string): JobSpec | undefined {
  return SPECS_BY_NAME.get(name);
}

export interface JobControlRow {
  readonly key: string;
  readonly value: Readonly<Record<string, unknown>>;
  /** ISO time. Absent on rows recorded before snapshots carried it. */
  readonly until?: string;
}

export interface EffectiveJobControls {
  readonly name: string;
  readonly paused: boolean;
  readonly pauseCause: 'none' | 'pause' | 'shed';
  /** No in-force row changes this job. Callers must keep the pre-change arguments. */
  readonly untouched: boolean;
  readonly shedLevel: 0 | 1 | 2;
  /** 1 when no boost row is in force. Presets are 2 and 4. */
  readonly boost: number;
  readonly knobs: Readonly<Record<string, number>>;
  /** `default` knobs must not be written over the env. `row` is an explicit set. `boost` is the preset. */
  readonly knobSources: Readonly<Record<string, 'default' | 'boost' | 'row'>>;
  /** Set only when a quiet_window row overrides the compiled window. */
  readonly quietOverride: { readonly empty: true } | JobWindow | null;
  /** Set only when a run_window row overrides the compiled window. */
  readonly runOverride: { readonly empty: true } | JobWindow | null;
}

export function shedLevelOf(value: unknown): 0 | 1 | 2 {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return 0;
  const level = (value as { level?: unknown }).level;
  if (level === 1 || level === 2) return level;
  return 0;
}

function rowValue(rows: readonly JobControlRow[], key: string): Readonly<Record<string, unknown>> | null {
  const found = rows.find((row) => row.key === key);
  return found === undefined ? null : found.value;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
}

function windowOf(value: Readonly<Record<string, unknown>>): { readonly empty: true } | JobWindow | null {
  if (value.empty === true) return { empty: true };
  const fromMinute = finiteNumber(value.from_minute);
  const toMinute = finiteNumber(value.to_minute);
  if (fromMinute === null || toMinute === null) return null;
  if (!Number.isInteger(fromMinute) || !Number.isInteger(toMinute)) return null;
  if (fromMinute < 0 || toMinute > 1440 || fromMinute >= toMinute) return null;
  return { fromMinute, toMinute };
}

function boostMultiplier(value: Readonly<Record<string, unknown>> | null): number {
  if (value === null) return 1;
  if (value.preset === '2' || value.preset === '4') return JOB_BOOST_PRESETS[value.preset];
  const multiplier = finiteNumber(value.multiplier);
  if (multiplier === null || multiplier < 1 || multiplier > JOB_BOOST_HARD_MAX) return 1;
  return multiplier;
}

export function shedPauses(priority: JobPriority, level: 0 | 1 | 2): boolean {
  if (priority === 'P0' || priority === 'P1') return false;
  if (level >= 2 && (priority === 'P2' || priority === 'P3')) return true;
  if (level >= 1 && priority === 'P3') return true;
  return false;
}

/** Resolve one job against in-force rows. Empty `rows` is untouched for every job. */
/**
 * Drop rows whose `until` is already past. Rows without `until` stay.
 * A present `until` that is not a time is kept and warned: dropping it
 * would clear a pause the writer did not expire.
 */
export function inForceJobControlRows(
  rows: readonly JobControlRow[],
  now = Date.now(),
  warn?: (key: string) => void,
): readonly JobControlRow[] {
  return rows.filter((row) => {
    if (typeof row.until !== 'string') return true;
    const at = Date.parse(row.until);
    if (!Number.isFinite(at)) {
      warn?.(row.key);
      return true;
    }
    return at > now;
  });
}

/** Headers control-loops sends when a fresh untouched snapshot decided this call. */
export interface JobControlCallerHint {
  readonly checked: string | undefined;
  readonly paused: string | undefined;
  readonly job: string | undefined;
}

/** The checked header for one job. The scheduler and the SQL-golden replay both use this. */
export function checkedJobControlHeaders(job: string, paused: '0' | '1' = '0'): Record<string, string> {
  return {
    'x-job-controls-checked': '1',
    'x-job-controls-paused': paused,
    'x-job-controls-job': job,
  };
}

export function jobControlCallerHint(header: (name: string) => string | undefined): JobControlCallerHint {
  return {
    checked: header('x-job-controls-checked'),
    paused: header('x-job-controls-paused'),
    job: header('x-job-controls-job'),
  };
}

/**
 * `run` is only `checked=1`, `paused=0`, and the header job equal to this route.
 * `paused` is only `checked=1`, `paused=1`, and the same job.
 * Anything else, including a missing or illegal value, reads the table.
 */
export function jobControlCallerDecision(hint: JobControlCallerHint | undefined, job: string): 'read' | 'run' | 'paused' {
  if (hint?.checked !== '1' || hint.job !== job) return 'read';
  if (hint.paused === '0') return 'run';
  if (hint.paused === '1') return 'paused';
  return 'read';
}

/** Gate for a caller that already checked. No rows, so no table read. */
export function callerDecidedGate(spec: JobSpec, decision: 'run' | 'paused'): EffectiveJobControls {
  const gate = resolveEffectiveJobControls(spec, []);
  if (decision === 'run') return gate;
  return { ...gate, paused: true, pauseCause: 'pause', untouched: false };
}

export function resolveEffectiveJobControls(
  spec: JobSpec,
  inputRows: readonly JobControlRow[],
  now = Date.now(),
): EffectiveJobControls {
  const rows = inForceJobControlRows(inputRows, now);
  const shed = shedLevelOf(rowValue(rows, 'shed') ?? {});
  const pauseRow = rowValue(rows, `job:${spec.name}:pause`);
  const shedPause = shedPauses(spec.priority, shed);
  const paused = pauseRow !== null || shedPause;
  const pauseCause = pauseRow !== null ? 'pause' : shedPause ? 'shed' : 'none';
  const boost = boostMultiplier(rowValue(rows, `job:${spec.name}:boost`));
  const knobs: Record<string, number> = {};
  const knobSources: Record<string, 'default' | 'boost' | 'row'> = {};
  let knobTouched = false;
  for (const item of spec.knobs) {
    const override = rowValue(rows, `job:${spec.name}:${item.name}`);
    const overrideNumber = override === null ? null : finiteNumber(override.n);
    let value = item.defaultValue;
    let source: 'default' | 'boost' | 'row' = 'default';
    if (item.boost && boost !== 1) {
      // Preset targets replace the multiply when the direction is wrong
      // (a shorter interval, a smaller recheck). boost === 1 never enters.
      const presetTarget = boost === 2 ? item.boostTargets?.['2'] : boost === 4 ? item.boostTargets?.['4'] : undefined;
      const raw = presetTarget !== undefined ? presetTarget : Math.floor(item.defaultValue * boost);
      const boosted = clamp(Math.floor(raw), item.min, item.max);
      if (boosted !== value) {
        value = boosted;
        source = 'boost';
        knobTouched = true;
      }
    }
    if (overrideNumber !== null) {
      value = clamp(Math.floor(overrideNumber), item.min, item.max);
      source = 'row';
      knobTouched = true;
    }
    knobs[item.name] = value;
    knobSources[item.name] = source;
  }
  const quietRaw = spec.quietWindow === undefined ? null : rowValue(rows, `job:${spec.name}:quiet_window`);
  const runRaw = spec.runWindow === undefined ? null : rowValue(rows, `job:${spec.name}:run_window`);
  const quietOverride = quietRaw === null ? null : windowOf(quietRaw);
  const runOverride = runRaw === null ? null : windowOf(runRaw);
  const untouched = !paused && !knobTouched && quietOverride === null && runOverride === null && !(shedPause);
  return {
    name: spec.name,
    paused,
    pauseCause,
    untouched,
    shedLevel: shed,
    boost,
    knobs,
    knobSources,
    quietOverride,
    runOverride,
  };
}

function envInteger(envValue: unknown): number | null {
  const raw = typeof envValue === 'number' && Number.isFinite(envValue)
    ? String(Math.trunc(envValue))
    : typeof envValue === 'string' ? envValue.trim() : '';
  if (!/^\d+$/u.test(raw)) return null;
  return Number(raw);
}

/**
 * Boost multiplies the live env integer when it sits inside the hard range.
 * `'keep'` means the env is an explicit pause (`"0"` on a knob whose minimum
 * is above 0) and must not be replaced. `null` means there is no in-range
 * env integer, so the caller keeps the compiled boost.
 */
export function boostedKnobFromEnv(
  item: JobKnobSpec,
  boost: number,
  envValue: unknown,
): number | 'keep' | null {
  if (typeof envValue === 'string' && envValue.trim() === '0' && item.min > 0) return 'keep';
  const parsed = envInteger(envValue);
  if (parsed === null || parsed < item.min || parsed > item.max) return null;
  return clamp(Math.floor(parsed * boost), item.min, item.max);
}

/** The number a handler should use, or the env value when the knob did not replace it. */
export function knobAgainstEnv(gate: EffectiveJobControls, name: string, envValue: unknown): unknown {
  if (gate.untouched) return envValue;
  const item = jobSpec(gate.name)?.knobs.find((knob) => knob.name === name);
  const source = gate.knobSources[name] ?? 'default';
  if (item === undefined || source === 'default') return envValue;
  if (source === 'boost') {
    const boosted = boostedKnobFromEnv(item, gate.boost, envValue);
    if (boosted === 'keep') return envValue;
    if (typeof boosted === 'number') return boosted;
  }
  const value = gate.knobs[name];
  return value === undefined ? envValue : value;
}

export interface PresentedJobControls extends EffectiveJobControls {
  /**
   * Knobs whose live base is another process's env. The number is omitted
   * from `knobs` so a list cannot show the compiled default as if it were live.
   */
  readonly knobBasis: Readonly<Record<string, string>>;
}

/** `jobs ls` view from one owner process. Foreign env keys are explained, not numbered. */
export function effectiveWithEnv(
  spec: JobSpec,
  rows: readonly JobControlRow[],
  env: Readonly<Record<string, unknown>>,
  here: 'platform' | 'gateway',
): PresentedJobControls {
  const gate = resolveEffectiveJobControls(spec, rows);
  const knobs: Record<string, number> = { ...gate.knobs };
  const knobBasis: Record<string, string> = {};
  for (const item of spec.knobs) {
    if (item.envKey === undefined || item.envHome === undefined) continue;
    const source = gate.knobSources[item.name] ?? 'default';
    if (source === 'row') continue;
    if (item.envHome !== here) {
      delete knobs[item.name];
      knobBasis[item.name] = `基础值 = ${item.envHome} 的 env ${item.envKey}（本处不可见）× ${gate.boost}`;
      continue;
    }
    if (!Object.prototype.hasOwnProperty.call(env, item.envKey)) continue;
    const envValue = env[item.envKey];
    if (source === 'default' || gate.untouched) {
      const parsed = envInteger(envValue);
      if (parsed !== null) knobs[item.name] = parsed;
      continue;
    }
    if (source === 'boost') {
      const boosted = boostedKnobFromEnv(item, gate.boost, envValue);
      if (boosted === 'keep') {
        const parsed = envInteger(envValue);
        if (parsed !== null) knobs[item.name] = parsed;
      } else if (typeof boosted === 'number') {
        knobs[item.name] = boosted;
      }
    }
  }
  return { ...gate, knobs, knobBasis };
}

export function insideMinuteWindow(nowMs: number, window: JobWindow): boolean {
  const shanghaiMinute = Math.floor((nowMs + 8 * 3_600_000) / 60_000) % 1440;
  return shanghaiMinute >= window.fromMinute && shanghaiMinute < window.toMinute;
}

const ACTOR = /^(service|operator|user):[^\s]+$/u;

export interface ControlWriteCommon {
  readonly reason: string;
  readonly forMs: number;
  readonly setBy: string;
}

export type ControlWriteInput = ControlWriteCommon & (
  | { readonly op: 'pause'; readonly name: string; readonly force?: boolean }
  | { readonly op: 'boost'; readonly name: string; readonly preset: string }
  | { readonly op: 'knob'; readonly name: string; readonly knob: string; readonly value: number }
  | { readonly op: 'quiet_window'; readonly name: string; readonly window: { readonly empty: true } | JobWindow }
  | { readonly op: 'run_window'; readonly name: string; readonly window: { readonly empty: true } | JobWindow }
  | { readonly op: 'shed'; readonly level: number }
);

export type ControlWritePlan =
  | { readonly ok: true; readonly key: string; readonly value: Readonly<Record<string, unknown>>; readonly clamped: boolean }
  | { readonly ok: false; readonly status: number; readonly code: string; readonly message: string };

function reject(status: number, code: string, message: string): Extract<ControlWritePlan, { ok: false }> {
  return { ok: false, status, code, message };
}

function checkCommon(input: ControlWriteCommon): ControlWritePlan | null {
  if (typeof input.reason !== 'string' || input.reason.trim() === '' || input.reason.length > 500) {
    return reject(400, 'bad_reason', 'reason is required and at most 500 characters');
  }
  if (!Number.isInteger(input.forMs) || input.forMs < 1 || input.forMs > JOB_CONTROL_MAX_MS) {
    return reject(400, 'bad_duration', 'for_ms must be an integer from 1 to 48 hours');
  }
  if (!ACTOR.test(input.setBy)) {
    return reject(400, 'bad_actor', 'set_by must look like operator:<id>');
  }
  return null;
}

function windowValue(window: { readonly empty: true } | JobWindow): ControlWritePlan | { readonly ok: true; readonly value: Record<string, unknown> } {
  if ('empty' in window && window.empty === true) return { ok: true, value: { empty: true } };
  if (!('fromMinute' in window)) return reject(400, 'bad_window', 'window needs fromMinute and toMinute, or empty');
  const { fromMinute, toMinute } = window;
  if (!Number.isInteger(fromMinute) || !Number.isInteger(toMinute) || fromMinute < 0 || toMinute > 1440 || fromMinute >= toMinute) {
    return reject(400, 'bad_window', 'window minutes must satisfy 0 <= from < to <= 1440');
  }
  return { ok: true, value: { from_minute: fromMinute, to_minute: toMinute } };
}

/** Validate a write. Does not touch a database. */
export function planControlWrite(input: ControlWriteInput): ControlWritePlan {
  const common = checkCommon(input);
  if (common !== null) return common;
  if (input.op === 'shed') {
    if (input.level !== 1 && input.level !== 2) return reject(400, 'bad_shed', 'shed level must be 1 or 2; use restore for 0');
    return { ok: true, key: 'shed', value: { level: input.level }, clamped: false };
  }
  const spec = jobSpec(input.name);
  if (spec === undefined) return reject(404, 'unknown_job', `unknown job ${input.name}`);
  if (input.op === 'pause') {
    if (spec.name === 'ledger_archive') {
      return reject(409, 'use_ledger_archive_pause', 'ledger_archive pause is the existing sentinel: PUT /v1/ops/ledger-archive/pause');
    }
    if (spec.priority === 'P0' && input.force !== true) {
      return reject(409, 'p0_pause_requires_force', `${spec.name} is P0; pause requires force`);
    }
    return { ok: true, key: `job:${spec.name}:pause`, value: input.force === true ? { force: true } : {}, clamped: false };
  }
  if (input.op === 'boost') {
    if (input.preset !== '2' && input.preset !== '4') return reject(400, 'bad_preset', 'boost preset must be 2 or 4');
    return { ok: true, key: `job:${spec.name}:boost`, value: { preset: input.preset }, clamped: false };
  }
  if (input.op === 'knob') {
    const item = spec.knobs.find((candidate) => candidate.name === input.knob);
    if (item === undefined) return reject(400, 'unknown_knob', `${spec.name} has no knob ${input.knob}`);
    if (!Number.isFinite(input.value)) return reject(400, 'bad_knob', 'knob value must be a finite number');
    if (item.k1Only === true) {
      return reject(409, 'k1_only_knob', `${spec.name} ${item.name} is ignored when consumers >= 2; the lane path does not read it`);
    }
    const clampedValue = clamp(Math.floor(input.value), item.min, item.max);
    if (item.accept !== undefined && !item.accept.some((range) => clampedValue >= range.min && clampedValue <= range.max)) {
      return reject(400, 'knob_off_range', `${spec.name} ${item.name} must be 0 or an integer in the open range; 1–4999 turns the lane off`);
    }
    return {
      ok: true,
      key: `job:${spec.name}:${item.name}`,
      value: { n: clampedValue },
      clamped: clampedValue !== input.value,
    };
  }
  if (input.op === 'quiet_window') {
    if (spec.quietWindow === undefined) return reject(400, 'no_window', `${spec.name} has no quiet window`);
    const planned = windowValue(input.window);
    if (!planned.ok) return planned;
    return { ok: true, key: `job:${spec.name}:quiet_window`, value: planned.value, clamped: false };
  }
  if (spec.runWindow === undefined) return reject(400, 'no_window', `${spec.name} has no run window`);
  const planned = windowValue(input.window);
  if (!planned.ok) return planned;
  return { ok: true, key: `job:${spec.name}:run_window`, value: planned.value, clamped: false };
}

export function jobControlKeys(name: string): readonly string[] {
  const spec = jobSpec(name);
  if (spec === undefined) return [];
  const keys = [`job:${name}:pause`, `job:${name}:boost`];
  for (const item of spec.knobs) keys.push(`job:${name}:${item.name}`);
  if (spec.quietWindow !== undefined) keys.push(`job:${name}:quiet_window`);
  if (spec.runWindow !== undefined) keys.push(`job:${name}:run_window`);
  return keys;
}

/** Lines an operator runs by hand. wcm does not read this shed (ADR-009). */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Parse an ops/internal JSON body. `setBy` is stamped by the caller, never the client. */
export function parseControlWriteBody(
  raw: unknown,
  setBy: string,
): ControlWriteInput | Extract<ControlWritePlan, { ok: false }> {
  if (!isRecord(raw)) return reject(400, 'bad_body', 'JSON object required');
  const reason = raw.reason;
  const forMs = raw.for_ms;
  if (typeof reason !== 'string' || typeof forMs !== 'number') {
    return reject(400, 'bad_body', 'reason and for_ms are required');
  }
  const op = raw.op;
  const common = { reason, forMs, setBy };
  if (op === 'shed') return { ...common, op, level: typeof raw.level === 'number' ? raw.level : Number.NaN };
  if (typeof raw.name !== 'string' || raw.name === '') return reject(400, 'bad_body', 'name is required');
  if (op === 'pause') return { ...common, op, name: raw.name, ...(raw.force === true ? { force: true } : {}) };
  if (op === 'boost') return { ...common, op, name: raw.name, preset: typeof raw.preset === 'string' ? raw.preset : '' };
  if (op === 'knob') {
    return {
      ...common,
      op,
      name: raw.name,
      knob: typeof raw.knob === 'string' ? raw.knob : '',
      value: typeof raw.value === 'number' ? raw.value : Number.NaN,
    };
  }
  if (op === 'quiet_window' || op === 'run_window') {
    const window = raw.window;
    if (!isRecord(window)) return reject(400, 'bad_window', 'window object required');
    if (window.empty === true) return { ...common, op, name: raw.name, window: { empty: true } };
    const fromMinute = window.from_minute;
    const toMinute = window.to_minute;
    if (typeof fromMinute !== 'number' || typeof toMinute !== 'number') {
      return reject(400, 'bad_window', 'window needs from_minute and to_minute, or empty: true');
    }
    return { ...common, op, name: raw.name, window: { fromMinute, toMinute } };
  }
  return reject(400, 'bad_op', 'op must be pause, boost, knob, quiet_window, run_window, or shed');
}

export function wcmShedCommands(forLabel: string, reason: string, actor = 'operator:ops'): readonly string[] {
  const by = actor.trim() === '' ? 'operator:ops' : actor.trim();
  const flags = `--for ${forLabel} --by ${by} --reason ${JSON.stringify(reason)}`;
  return [
    `wcm limits set --key slots --value 1 ${flags}`,
    `wcm limits set --key workers --value 1 ${flags}`,
    `wcm sprint --clear --by ${by} --reason ${JSON.stringify(reason)}`,
  ];
}
